import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, concatMap, from, map, of, switchMap } from "rxjs";
import { loadTenantAppCatalogApps, loadTenantAppCatalogAppsFailed, loadTenantAppCatalogAppsSuccess, loadTenantInternalSettings, loadTenantInternalSettingsFailed, loadTenantInternalSettingsSuccess, loadTenantSharingSettings, loadTenantSharingSettingsFailed, loadTenantSharingSettingsSuccess, loadTenantBrandCenter, loadTenantBrandCenterFailed, loadTenantBrandCenterSuccess, loadTenantSensitivityLabels, loadTenantSensitivityLabelsFailed, loadTenantSensitivityLabelsSuccess, loadTenantRetentionLabels, loadTenantRetentionLabelsFailed, loadTenantRetentionLabelsSuccess, loadTenantGroupSettings, loadTenantGroupSettingsFailed, loadTenantGroupSettingsSuccess } from "../actions/tenant-information.actions";
import { TenantsService } from "src/app/services/tenants/tenants.service";
import { ConsentsPermissionsService } from "src/app/modules/workspace/services/consents-permissions/consents-permissions.service";
import { WorkspaceService } from "src/app/services/workspace/workspace.service";
import { GROUP_SETTING_TEMPLATE_ID } from "src/app/models/constants.model";

@Injectable()
export class TenantInfoEffects {
	private actions$ = inject(Actions);
	private tenantService = inject(TenantsService);
	private consentsService = inject(ConsentsPermissionsService);
	private workspaceService = inject(WorkspaceService);

	// Load the app catalog apps from backend blob storage
	loadTenantAppCatalogApps$ = createEffect(() =>
		this.actions$.pipe(
			ofType(loadTenantAppCatalogApps),
			switchMap(action =>
				from(this.consentsService.getUserConsentByApplication(action.application)).pipe(
					switchMap(application => {
						if (!application) {
							return of(
								loadTenantAppCatalogAppsFailed({ error: 'Application not found' })
							);
						}

						return this.tenantService.loadTenantAppCatalogApps(action.application, application.tenant.id, application.key)
							.pipe(
								map(resp =>
									loadTenantAppCatalogAppsSuccess({ appCatalogApps: resp.apps, tenantAppCatalogUri: resp.tenantAppCatalogUri })
								),
								catchError(error =>
									of(loadTenantAppCatalogAppsFailed({ error }))
								)
							);
					})
				)
			)
		)
	);

	// Load the workflows from backend blob storage
	loadTenantSharingSettings$ = createEffect(() =>
		this.actions$.pipe(
			ofType(loadTenantSharingSettings),
			switchMap(action =>
				from(
					this.consentsService.getUserConsentByApplication(action.application)
				).pipe(
					switchMap(application => {
						if (!application) {
							return of(
								loadTenantSharingSettingsFailed({
									error: 'Application not found'
								})
							);
						}

						return this.tenantService
							.loadTenantSharingSettings(
								application.key,
								application.tenant.id,
								action.application
							)
							.pipe(
								map(resp =>
									loadTenantSharingSettingsSuccess({
										sharingSettings: resp.data
									})
								),
								catchError(error =>
									of(loadTenantSharingSettingsFailed({ error }))
								)
							);
					})
				)
			)
		)
	);

	// Load the internal settings from backend blob storage
	loadTenantInternalSettings$ = createEffect(() =>
		this.actions$.pipe(
			ofType(loadTenantInternalSettings),
			switchMap(action =>
				from(
					this.consentsService.getUserConsentByApplication(action.application)
				).pipe(
					switchMap(application => {
						if (!application) {
							return of(
								loadTenantInternalSettingsFailed({
									error: 'Application not found'
								})
							);
						}

						return this.tenantService
							.loadTenantInternalSettings(
								application.key,
								application.tenant.id,
								action.application
							)
							.pipe(
								map(resp =>
									loadTenantInternalSettingsSuccess({
										internalSettings: resp.data
									})
								),
								catchError(error =>
									of(loadTenantInternalSettingsFailed({ error }))
								)
							);
					})
				)
			)
		)
	);


	// Load the Brand Center status from backend
	loadTenantBrandCenter$ = createEffect(() =>
		this.actions$.pipe(
			ofType(loadTenantBrandCenter),
			switchMap(action =>
				from(this.consentsService.getUserConsentByApplication(action.application)).pipe(
					switchMap(application => {
						if (!application) {
							return of(loadTenantBrandCenterFailed({ error: 'Application not found' }));
						}

						return from(this.tenantService.loadBrandCenterStatus(action.application)).pipe(
							map(resp => loadTenantBrandCenterSuccess({ brandCenter: resp })),
							catchError(error => of(loadTenantBrandCenterFailed({ error })))
						);
					}
					)
				)
			)
		));

	loadTenantSensitivityLabels$ = createEffect(() =>
		this.actions$.pipe(
			ofType(loadTenantSensitivityLabels),
			concatMap(action =>
				from(this.consentsService.getUserConsentByApplication(action.application)).pipe(
					switchMap(application => {
						if (!application) {
							return of(loadTenantSensitivityLabelsFailed({ error: 'Application not found' }));
						}

						return from(this.tenantService.getSensitivityLabelsFromAPI(action.application)).pipe(
							map(sensitivityLabels => loadTenantSensitivityLabelsSuccess({ sensitivityLabels }))
						);
					}),
					catchError(error => of(loadTenantSensitivityLabelsFailed({ error })))
				)
			)
		)
	);

	loadTenantGroupSettings$ = createEffect(() =>
		this.actions$.pipe(
			ofType(loadTenantGroupSettings),
			switchMap(action =>
				from(this.consentsService.getUserConsentByApplication(action.application)).pipe(
					switchMap(application => {
						if (!application) {
							return of(loadTenantGroupSettingsFailed({ error: 'Application not found' }));
						}

						return from(this.tenantService.getTenantGroupSettingsFromAPI(action.application)).pipe(
							map(groupSettings => {
								const groupSetting = groupSettings.find(x => x.templateId === GROUP_SETTING_TEMPLATE_ID);
								const enabled = groupSetting?.values?.some(x => x.name?.toLowerCase() === 'enablemiplabels' && x.value?.toLowerCase() === 'true') ?? false;
								void this.workspaceService.updateSensitivityLabelStatus(enabled ? 'Enabled' : 'Disabled');
								return loadTenantGroupSettingsSuccess({ groupSettings });
							}),
							catchError(error => of(loadTenantGroupSettingsFailed({ error })))
						);
					})
				)
			)
		)
	);

	loadTenantRetentionLabels$ = createEffect(() =>
		this.actions$.pipe(
			ofType(loadTenantRetentionLabels),
			switchMap(action =>
				from(this.consentsService.getUserConsentByApplication(action.application)).pipe(
					switchMap(application => {
						if (!application) {
							return of(loadTenantRetentionLabelsFailed({ error: 'Application not found' }));
						}

						return from(this.tenantService.getRetentionPoliciesFromAPI(!!action.forceRefresh, action.application)).pipe(
							map(retentionLabels => loadTenantRetentionLabelsSuccess({ retentionLabels })),
							catchError(error => of(loadTenantRetentionLabelsFailed({ error })))
						);
					})
				)
			)
		)
	);
}
